import AuthLayout from '../../components/layout/AuthLayout.jsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDownloadCalendarMeetingDetailsMutation, useGetCalendarMeetingQuery } from '../../api/calendar.js';
import React, { useMemo, useState } from 'react';
import BaseButton from '../../components/form/BaseButton.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { TAG_MEETING } from '../../api/tags.js';
import { api } from '../../api/api.js';
import { getMeetingStatus } from '../../utils/meetingStatus.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import dayjs from 'dayjs';
import AttendeesSummaryCarousel from '../../components/misc/AttendeesSummaryCarousel.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import MeetingNotes from '../../components/misc/MeetingNotes.jsx';
import MeetingContext from '../../components/misc/MeetingContext.jsx';
import IconLocation from '../../components/images/IconLocation.jsx';
import IconTime from '../../components/images/IconTime.jsx';
import IconChecked from '../../components/images/IconChecked.jsx';
import ModelShared from '../../components/misc/ModelShared.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import {
    PERMISSION_CALENDAR,
    PERMISSION_CONTACTS,
    PERMISSION_OWNED_ORGANIZATIONS,
    PERMISSION_PARTNER_ORGANIZATIONS,
    PERMISSION_PROJECTS
} from '../../utils/constants.js';
import { formatStatus, getStatusColor } from '../../utils/calendar.js';
import MeetingCancelConfirmation from '../../components/misc/MeetingCancelConfirmation.jsx';
import MeetingParticipantsInvitation from '../../components/misc/MeetingParticipantsInvitation.jsx';
import IconDescription from '../../components/images/IconDescription.jsx';
import { useInfoToast } from '../../hooks/useToast.jsx';
import OrganizationsAIInfoOverlay from '../../components/misc/OrganizationsAIInfoOverlay.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';

import PageNotFound from '../misc/PageNotFound.jsx';
import BaseError from '../../components/misc/BaseError.jsx';
import { HTTP_NOT_FOUND } from '../../utils/constants.js';

const ViewMeeting = () => {
    const { permissions: userPermissions } = useCurrentUser();
    const [cancelVisible, setCancelVisible] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [inviteModal, setInviteModal] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [showOrgInfoOverlay, setShowOrgInfoOverlay] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState(null);

    const { id } = useParams();
    const user = useSelector((state) => state.user.user);

    const { data: meetingResponse, isLoading, isError, error } = useGetCalendarMeetingQuery(id);

    const calendarMeeting = useMemo(() => {
        if (meetingResponse?.response?.meeting) {
            setParticipants(meetingResponse.response.meeting.participants);
            return meetingResponse.response.meeting;
        }

        return null;
    }, [meetingResponse]);

    const { started, ended, status } = getMeetingStatus(calendarMeeting);
    const [downloadMeetingDetails, { isLoadingPDF }] = useDownloadCalendarMeetingDetailsMutation();

    const meetingPreparation = () => {
        dispatch(api.util.invalidateTags([TAG_MEETING]));
        navigate(`/calendar/preparation/${calendarMeeting?.id}`);
    };

    const downloadPdf = () => {
        downloadMeetingDetails({ id: calendarMeeting?.id, name: calendarMeeting?.title.replaceAll(' ', '_') });
    };

    const handleCancelConfirmation = () => {
        setCancelVisible(true);
    };

    if (isLoading) {
        return (
            <AuthLayout title='Calendar' backRoute='/calendar'>
                <div className='text-center mt-[100px]'>
                    <ThreeDotsLoader />
                    <div className='text-theme1-primary font-bold text-xl mt-[10px]'>Loading...</div>
                </div>
            </AuthLayout>
        );
    }

    if (isError && error?.status === HTTP_NOT_FOUND) {
        return <PageNotFound />;
    }

    if (isError) {
        return (
            <AuthLayout title='Calendar' backRoute='/calendar'>
                <BaseError status={error?.status || 500} statusText={error?.data?.message || error?.statusText} />
            </AuthLayout>
        );
    }

    const isValidUrl = (string) => {
        try {
            return Boolean(new URL(string));
        } catch (e) {
            return false;
        }
    };

    const handleCopy = (text) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(text)
                .then(() => useInfoToast('Copied to clipboard!'))
                .catch((err) => console.error('Clipboard copy failed', err));
        } else {
            try {
                document.execCommand('copy');
                useInfoToast('Copied link to clipboard!');
            } catch (err) {
                console.error('Fallback copy failed', err);
            }
        }
    };

    const getStartAndEndDateTime = () => {
        if (calendarMeeting) {
            let date = '';

            if (calendarMeeting.start_date) {
                if (calendarMeeting.start_date !== calendarMeeting.end_date) {
                    date += 'From ' + dayjs(new Date(calendarMeeting.start_date)).format('dddd, MMM DD, YYYY');
                    if (calendarMeeting.start_time) {
                        date += ' at ' + dayjs(new Date(calendarMeeting.start_time)).format('HH:mm');
                    }

                    if (calendarMeeting.end_date) {
                        date += '\nTo ' + dayjs(new Date(calendarMeeting.end_date)).format('dddd, MMM DD, YYYY');
                        if (calendarMeeting.end_time) {
                            date += ' at ' + dayjs(new Date(calendarMeeting.end_time)).format('HH:mm');
                        }
                    }
                } else {
                    date += dayjs(new Date(calendarMeeting.start_date)).format('dddd, MMM DD, YYYY');
                    if (calendarMeeting.start_time) {
                        date += ' from ' + dayjs(new Date(calendarMeeting.start_time)).format('HH:mm');
                    }
                    if (calendarMeeting.end_time) {
                        date += ' to ' + dayjs(new Date(calendarMeeting.end_time)).format('HH:mm');
                    }
                }
            }

            return date;
        }

        return null;
    };

    const handleOrgInfoOverlay = (org) => {
        setSelectedOrganization(org);
        setShowOrgInfoOverlay(true);
    };

    if (!calendarMeeting) return null;

    return (
        <AuthLayout title='Calendar' backRoute='/calendar'>
            <div className='flex flex-col max-w-3xl w-full mx-auto px-8 border-b border-theme1-cardStroke'>
                <div className='flex justify-between items-center py-4'>
                    <div></div>
                    <div className='font-bold text-theme1-dark'>Meeting Details</div>
                    {!!userPermissions[PERMISSION_CALENDAR]?.update &&
                    !ended &&
                    calendarMeeting.status !== 'canceled' &&
                    canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'update') ? (
                        <div
                            onClick={() => navigate(`/calendar/edit/${calendarMeeting.id}`)}
                            className='text-theme1-dark'
                        >
                            Edit
                        </div>
                    ) : (
                        <div></div>
                    )}
                </div>
            </div>
            <div className='flex flex-col max-w-3xl w-full mx-auto px-8'>
                <div className='mt-4'>
                    <span
                        className={`text-[10px] text-white rounded-md py-1 px-2 font-extrabold ${getStatusColor(calendarMeeting.status)}`}
                    >
                        {formatStatus(calendarMeeting.status)}
                    </span>
                    <div className='text-theme1-primary font-bold text-xl mt-2'>{calendarMeeting.title}</div>
                    {(calendarMeeting.location || calendarMeeting.country_id) && (
                        <>
                            {calendarMeeting.location && isValidUrl(calendarMeeting.location) ? (
                                <div className='card flex justify-between align-items-center gap-3'>
                                    <div className='flex align-items-center gap-2'>
                                        <svg
                                            width='40'
                                            height='39'
                                            viewBox='0 0 40 39'
                                            fill='none'
                                            xmlns='http://www.w3.org/2000/svg'
                                        >
                                            <svg
                                                width='40'
                                                height='39'
                                                viewBox='0 0 40 39'
                                                fill='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <svg
                                                    width='40'
                                                    height='39'
                                                    viewBox='0 0 40 39'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <g clipPath='url(#clip0_7760_6599)'>
                                                        <mask
                                                            id='mask0_7760_6599'
                                                            style={{ maskType: 'luminance' }}
                                                            maskUnits='userSpaceOnUse'
                                                            x='0'
                                                            y='0'
                                                            width='39'
                                                            height='39'
                                                        >
                                                            <path
                                                                d='M19.4879 38.6667C29.876 38.6667 38.2972 30.1601 38.2972 19.6667C38.2972 9.17325 29.876 0.666664 19.4879 0.666664C9.09989 0.666664 0.678711 9.17325 0.678711 19.6667C0.678711 30.1601 9.09989 38.6667 19.4879 38.6667Z'
                                                                fill='white'
                                                            />
                                                        </mask>
                                                        <g mask='url(#mask0_7760_6599)'>
                                                            <path
                                                                d='M-0.246094 -0.267601H39.2144V39.5931H-0.246094V-0.267601Z'
                                                                fill='#E5E5E4'
                                                            />
                                                        </g>
                                                        <mask
                                                            id='mask1_7760_6599'
                                                            style={{ maskType: 'luminance' }}
                                                            maskUnits='userSpaceOnUse'
                                                            x='1'
                                                            y='1'
                                                            width='37'
                                                            height='38'
                                                        >
                                                            <path
                                                                d='M19.4879 38.2898C29.6699 38.2898 37.9241 29.952 37.9241 19.6667C37.9241 9.38142 29.6699 1.04356 19.4879 1.04356C9.30591 1.04356 1.05176 9.38142 1.05176 19.6667C1.05176 29.952 9.30591 38.2898 19.4879 38.2898Z'
                                                                fill='white'
                                                            />
                                                        </mask>
                                                        <g mask='url(#mask1_7760_6599)'>
                                                            <path
                                                                d='M0.126953 0.10923H38.8491V39.2241H0.126953V0.10923Z'
                                                                fill='white'
                                                            />
                                                            <mask
                                                                id='mask2_7760_6599'
                                                                style={{ maskType: 'luminance' }}
                                                                maskUnits='userSpaceOnUse'
                                                                x='2'
                                                                y='2'
                                                                width='35'
                                                                height='35'
                                                            >
                                                                <path
                                                                    d='M19.4877 36.7981C28.8541 36.7981 36.4471 29.1281 36.4471 19.6667C36.4471 10.2052 28.8541 2.53526 19.4877 2.53526C10.1213 2.53526 2.52832 10.2052 2.52832 19.6667C2.52832 29.1281 10.1213 36.7981 19.4877 36.7981Z'
                                                                    fill='white'
                                                                />
                                                            </mask>
                                                            <g mask='url(#mask2_7760_6599)'>
                                                                <path
                                                                    d='M1.60352 1.60091H37.3721V37.7323H1.60352V1.60091Z'
                                                                    fill='#4A8CFF'
                                                                />
                                                            </g>
                                                            <mask
                                                                id='mask3_7760_6599'
                                                                style={{ maskType: 'luminance' }}
                                                                maskUnits='userSpaceOnUse'
                                                                x='8'
                                                                y='13'
                                                                width='23'
                                                                height='13'
                                                            >
                                                                <path
                                                                    d='M24.6497 17.4447L29.4453 13.9038C29.865 13.5583 30.1836 13.6369 30.1836 14.2728V25.0683C30.1836 25.7827 29.7873 25.7042 29.4453 25.4373L24.6497 21.8964V17.4447ZM8.4209 14.3042V22.3439C8.42867 24.1654 9.89765 25.6335 11.7009 25.6257H23.3206C23.647 25.6257 23.9113 25.3588 23.9191 25.029V16.9815C23.9113 15.16 22.4423 13.6918 20.6391 13.6997H9.01937C8.69293 13.7075 8.42867 13.9666 8.4209 14.3042Z'
                                                                    fill='white'
                                                                />
                                                            </mask>
                                                            <g mask='url(#mask3_7760_6599)'>
                                                                <path
                                                                    d='M7.50391 12.7731H31.1087V26.552H7.50391V12.7731Z'
                                                                    fill='white'
                                                                />
                                                            </g>
                                                        </g>
                                                    </g>
                                                    <defs>
                                                        <clipPath id='clip0_7760_6599'>
                                                            <rect
                                                                width='40'
                                                                height='38'
                                                                fill='white'
                                                                transform='translate(0 0.666664)'
                                                            />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                            </svg>
                                        </svg>
                                    </div>
                                    <div>
                                        <BaseButton
                                            label='Link'
                                            className='text-theme1-primary bg-theme1-pinkPrimary w-50 p-3 mr-[10px]'
                                            textValue={'Join'}
                                            link
                                            onClick={() => window.open(calendarMeeting.location, '_blank')}
                                        />
                                        <BaseButton
                                            label='Link'
                                            className='text-theme1-primary bg-theme1-pinkPrimary w-50 p-3'
                                            textValue={
                                                <svg
                                                    width='14'
                                                    height='18'
                                                    viewBox='0 0 14 18'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <svg
                                                        width='14'
                                                        height='18'
                                                        viewBox='0 0 14 18'
                                                        fill='none'
                                                        xmlns='http://www.w3.org/2000/svg'
                                                    >
                                                        <svg
                                                            width='14'
                                                            height='18'
                                                            viewBox='0 0 14 18'
                                                            fill='none'
                                                            xmlns='http://www.w3.org/2000/svg'
                                                        >
                                                            <path
                                                                d='M2.77393 17.5304C1.96338 17.5304 1.3457 17.3205 0.920898 16.9006C0.500977 16.4806 0.291016 15.8703 0.291016 15.0695V7.93571C0.291016 7.13004 0.500977 6.51969 0.920898 6.10465C1.3457 5.68473 1.96338 5.47477 2.77393 5.47477H4.86133V7.10075H2.90576C2.5835 7.10075 2.33691 7.18375 2.16602 7.34977C2 7.5109 1.91699 7.75993 1.91699 8.09684V14.901C1.91699 15.238 2 15.487 2.16602 15.6481C2.33691 15.8141 2.5835 15.8971 2.90576 15.8971H11.0869C11.4043 15.8971 11.6484 15.8141 11.8193 15.6481C11.9902 15.487 12.0757 15.238 12.0757 14.901V8.09684C12.0757 7.75993 11.9902 7.5109 11.8193 7.34977C11.6484 7.18375 11.4043 7.10075 11.0869 7.10075H9.13135V5.47477H11.2261C12.0366 5.47477 12.6519 5.68473 13.0718 6.10465C13.4966 6.51969 13.709 7.13004 13.709 7.93571V15.0695C13.709 15.8703 13.4966 16.4806 13.0718 16.9006C12.6519 17.3205 12.0366 17.5304 11.2261 17.5304H2.77393ZM6.99268 11.5026C6.7876 11.5026 6.61182 11.4294 6.46533 11.2829C6.31885 11.1364 6.24561 10.9655 6.24561 10.7702V3.48258L6.31152 2.40592L5.94531 2.89664L4.96387 3.94401C4.82715 4.08561 4.65869 4.15641 4.4585 4.15641C4.28271 4.15641 4.12891 4.09782 3.99707 3.98063C3.87012 3.86344 3.80664 3.71207 3.80664 3.52653C3.80664 3.35075 3.875 3.19205 4.01172 3.05045L6.41406 0.743324C6.5166 0.645668 6.61426 0.57975 6.70703 0.54557C6.7998 0.506508 6.89502 0.486977 6.99268 0.486977C7.09521 0.486977 7.19287 0.506508 7.28564 0.54557C7.3833 0.57975 7.48096 0.645668 7.57861 0.743324L9.98096 3.05045C10.1177 3.19205 10.186 3.35075 10.186 3.52653C10.186 3.71207 10.1201 3.86344 9.98828 3.98063C9.85645 4.09782 9.70264 4.15641 9.52686 4.15641C9.32666 4.15641 9.16064 4.08561 9.02881 3.94401L8.05469 2.89664L7.68848 2.40592L7.74707 3.48258V10.7702C7.74707 10.9655 7.67383 11.1364 7.52734 11.2829C7.38574 11.4294 7.20752 11.5026 6.99268 11.5026Z'
                                                                fill='#921515'
                                                            />
                                                        </svg>
                                                    </svg>
                                                </svg>
                                            }
                                            link
                                            onClick={() => handleCopy(calendarMeeting.location)}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className='text-theme1-title2 font-semibold flex items-center mt-2'>
                                    <IconLocation className='mr-2 fill-theme1-title2' />
                                    <span>
                                        {calendarMeeting.country_id
                                            ? `${calendarMeeting.country.code}${calendarMeeting.location ? `, ${calendarMeeting.location}` : ''}`
                                            : calendarMeeting.location}
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                    {calendarMeeting.description && (
                        <div className='text-theme1-title2 font-semibold flex items-center mt-2'>
                            <IconDescription className='mr-2 fill-theme1-title2' />
                            <span>{calendarMeeting.description}</span>
                        </div>
                    )}
                </div>
                <div className='mt-4 flex items-center'>
                    <IconTime className='mr-2' />
                    <div className='text-theme1-title2 whitespace-pre-wrap'>{getStartAndEndDateTime()}</div>
                </div>
            </div>
            {calendarMeeting && (
                <div className='max-w-3xl w-full mx-auto px-8 py-8 mt-4 bg-theme1-pinkGray'>
                    <div className='text-lg font-bold text-theme1-text2 mb-1'>
                        Shared within <span className='text-theme1-brightRed'>*</span>
                    </div>

                    <ModelShared
                        shared={calendarMeeting.shared}
                        sharedUsers={calendarMeeting.shared_users}
                        sharedOrganizations={calendarMeeting.shared_organizations}
                        user={user}
                        permission={PERMISSION_CALENDAR}
                    />
                </div>
            )}
            {calendarMeeting?.is_recurrent && (
                <div className='max-w-3xl w-full mx-auto px-8 mt-4'>
                    <div className='text-lg font-bold text-theme1-dark mb-2'>Meeting Recurrence</div>
                    <div className='flex items-center mb-2 gap-x-2 flex-wrap'>
                        <div className='text-theme1-title2 font-semibold'>Meeting repeats:</div>
                        <div className='text-theme1-title2 capitalize'>{calendarMeeting.frequency}</div>
                    </div>
                    {(calendarMeeting.frequency === 'weekly' || calendarMeeting.frequency === 'monthly') && (
                        <div className='flex items-center mb-2 gap-x-2 flex-wrap'>
                            <div className='text-theme1-title2 font-semibold'>Meeting repeats on:</div>
                            {calendarMeeting.frequency === 'weekly' && (
                                <div className='text-theme1-title2 capitalize'>
                                    {calendarMeeting.week_days.map((day) => day).join(', ')}
                                </div>
                            )}
                            {calendarMeeting.frequency === 'monthly' && (
                                <div className='text-theme1-title2 capitalize'>
                                    {calendarMeeting.month_days.map((day) => day).join(', ')}
                                </div>
                            )}
                        </div>
                    )}
                    {calendarMeeting.recurrence_end_type !== 'never' && (
                        <div className='flex items-center mb-2 gap-x-2 flex-wrap'>
                            <div className='text-theme1-title2 font-semibold'>Recurrence ends on:</div>
                            <div className='text-theme1-title2 capitalize'>
                                {dayjs(calendarMeeting.recurrence_end_date).format('dddd, MMM D, YYYY [at] HH:mm')}
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className='max-w-3xl w-full mx-auto px-8 mt-4'>
                {userPermissions[PERMISSION_CONTACTS]?.read && participants.length > 0 && (
                    <>
                        <div className='text-lg font-bold text-theme1-dark mb-4'>Meeting Attendees</div>

                        <AttendeesSummaryCarousel
                            calendarMeetingId={calendarMeeting.id}
                            items={participants}
                            className=''
                            noEdit
                        />
                    </>
                )}
            </div>
            <div className='max-w-3xl w-full mx-auto px-8 mt-4'>
                <div className='text-lg font-bold text-theme1-dark mb-4'>Meeting Objectives</div>
                <BaseCard className='m-0 mb-10 p-4 bg-theme1-white'>
                    {!ended && (
                        <MeetingNotes
                            calendarMeetingId={calendarMeeting?.id}
                            calendarMeetingUserNotes={calendarMeeting?.objectives}
                            calendarMeetingAINotes={calendarMeeting?.suggested_objectives}
                            nothingFound='No objectives found'
                            editButtonText='Edit Objectives'
                            highlightText='AI-Speculated Objectives'
                            notesType='objectives'
                            noEdit
                        />
                    )}
                    {ended && (
                        <>
                            {calendarMeeting?.objectives?.length > 0 &&
                                calendarMeeting?.objectives?.map((objective) => (
                                    <div key={objective.id} className='flex justify-between py-1 w-full'>
                                        <span
                                            className={`text-theme1-goldDark text-[16px] max-w-[90%] ${objective.is_met ? 'font-bold' : ''}`}
                                        >
                                            {objective.text}
                                        </span>
                                        <span className='flex space-x-4 items-start mt-[3px]'>
                                            {objective.is_met ? (
                                                <IconChecked className='w-8 h-8 fill-theme1-goldDark' />
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </div>
                                ))}
                            {(!calendarMeeting?.objectives || calendarMeeting?.objectives?.length === 0) && (
                                <div className='text-theme1-text1'>No objectives found</div>
                            )}
                        </>
                    )}
                </BaseCard>

                <div className='text-lg font-bold text-theme1-dark mb-4'>Meeting Obstacles</div>

                <BaseCard className='m-0 mb-10 p-4 bg-theme1-white'>
                    <MeetingNotes
                        calendarMeetingId={calendarMeeting?.id}
                        calendarMeetingUserNotes={calendarMeeting?.obstacles}
                        calendarMeetingAINotes={!ended ? calendarMeeting?.suggested_obstacles : []}
                        nothingFound='No obstacles found'
                        editButtonText='Edit Obstacles'
                        highlightText='AI-Speculated Obstacles'
                        notesType='obstacles'
                        noEdit
                    />
                </BaseCard>

                <div className='text-lg font-bold text-theme1-dark mb-4'>Meeting Context</div>

                <BaseCard className='m-0 mb-10 p-4 bg-theme1-white'>
                    <MeetingContext
                        calendarMeetingId={calendarMeeting?.id}
                        calendarMeetingUserContext={calendarMeeting?.context}
                        calendarMeetingAIContext={!ended ? calendarMeeting?.suggested_context : null}
                        highlightText='AI-Speculated Context'
                        noEdit
                    />
                </BaseCard>

                {ended && calendarMeeting?.actions && (
                    <>
                        <div className='text-lg font-bold text-theme1-dark mb-4'>Actions</div>

                        <BaseCard className='m-0 mb-6 p-4 bg-theme1-white'>
                            <div className='text-theme1-text1 mt-1 whitespace-pre-wrap'>{calendarMeeting?.actions}</div>
                        </BaseCard>
                    </>
                )}
            </div>
            {userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.read &&
                userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.read &&
                calendarMeeting?.organizations?.length > 0 && (
                    <BaseCard className='border-0 bg-theme1-pinkGray p-0 pb-6'>
                        <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                            <div className='text-2xl font-bold text-theme1-title2'>Organizations</div>
                        </div>
                        <BaseCard className='border-0 m-0 py-0 px-0 mx-8 w-auto'>
                            <div className='max-w-3xl w-full mx-auto px-4 pb-4'>
                                {calendarMeeting.organizations.map((organization) => {
                                    return (
                                        <div key={organization.id} className='w-full flex items-center pt-4 gap-4'>
                                            <div className='flex-grow text-md text-theme1-text1'>
                                                <Link to={`/organizations/${organization.organization_id}`}>
                                                    {organization.name}
                                                </Link>
                                            </div>
                                            {organization.openai_info && (
                                                <BaseButton
                                                    className='bg-theme1-primary p-2 px-3 sm:px-5 text-white text-sm sm:text-base w-auto flex-shrink-0'
                                                    textValue='View info'
                                                    onClick={() => handleOrgInfoOverlay(organization)}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </BaseCard>
                    </BaseCard>
                )}
            {userPermissions[PERMISSION_PROJECTS]?.read && calendarMeeting?.projects?.length > 0 && (
                <BaseCard className='border-0 bg-theme1-pinkGray p-0 pb-6'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Projects</div>
                    </div>
                    <BaseCard className='border-0 m-0 py-0 px-0 mx-8 w-auto'>
                        <div className='max-w-3xl w-full mx-auto px-4 pb-4'>
                            {calendarMeeting.projects.map((project) => {
                                return (
                                    <div key={project.id} className='w-full flex pt-4 items-center'>
                                        <div className='flex-grow text-md text-theme1-text1'>
                                            <Link to={`/projects/${project.project_id}`}>{project.name}</Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </BaseCard>
                </BaseCard>
            )}
            {calendarMeeting?.files?.length > 0 && (
                <BaseCard className='border-0 bg-theme1-pinkGray p-0 pb-6'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Files</div>
                    </div>
                    <BaseCard className='border-0 m-0 py-0 px-0 mx-8 w-auto'>
                        <div className='max-w-3xl w-full mx-auto px-4 pb-4'>
                            {calendarMeeting.files.map((file) => {
                                return (
                                    <div key={file.id} className='w-full flex pt-4 items-center'>
                                        <a className='flex-grow text-md text-theme1-text1' href={file.url}>
                                            {file.title}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </BaseCard>
                </BaseCard>
            )}
            {calendarMeeting?.notes?.length > 0 && (
                <BaseCard className='border-0 bg-theme1-pinkGray p-0 pb-6'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Notes</div>
                    </div>
                    <BaseCard className='border-0 m-0 py-0 px-0 mx-8 w-auto'>
                        <div className='max-w-3xl w-full mx-auto px-4 pb-4'>
                            {calendarMeeting.notes.map((note) => {
                                return (
                                    <div key={note.id} className='w-full flex pt-4 items-center'>
                                        <div className='text-theme1-primary font-bold text-xs mr-4 text-center'>
                                            {dayjs(new Date(note.created_at)).format('DD MMM YYYY')}
                                        </div>
                                        <div className='flex-grow text-sm text-theme1-text1'>{note.note}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </BaseCard>
                </BaseCard>
            )}
            <div className='flex flex-col max-w-3xl w-full mx-auto px-8 mt-8 mb-6'>
                <div>
                    <div className='flex flex-col justify-center items-center'>
                        <BaseButton
                            className='btn-primary'
                            onClick={downloadPdf}
                            textValue={'Export Meeting Details'}
                        />
                    </div>
                </div>
            </div>

            {!!userPermissions[PERMISSION_CALENDAR]?.update &&
                calendarMeeting.status !== 'canceled' &&
                canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'update') && (
                    <div className='flex flex-col max-w-3xl w-full mx-auto px-8 mb-6'>
                        <div>
                            <div className='flex flex-col justify-center items-center'>
                                <BaseButton className='btn-primary' onClick={meetingPreparation} textValue={status} />
                            </div>
                        </div>
                    </div>
                )}

            {!!userPermissions[PERMISSION_CALENDAR]?.update &&
                new Date() < new Date(calendarMeeting.start_time ?? calendarMeeting.start_date) &&
                canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'update') && (
                    <div className='flex flex-col max-w-3xl w-full mx-auto px-8 mb-6'>
                        <div>
                            <div className='flex flex-col justify-center items-center'>
                                <BaseButton
                                    className='btn-primary'
                                    onClick={() => setInviteModal(true)}
                                    textValue={'Invite & Notify'}
                                />
                            </div>
                        </div>
                    </div>
                )}

            {!!userPermissions[PERMISSION_CALENDAR]?.update &&
                new Date() < new Date(calendarMeeting.start_time ?? calendarMeeting.start_date) &&
                canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'update') &&
                participants &&
                participants.length > 0 && (
                    <MeetingParticipantsInvitation
                        visible={inviteModal}
                        setVisible={setInviteModal}
                        calendarContacts={participants}
                        setCalendarContacts={setParticipants}
                        meetingId={calendarMeeting.id}
                    />
                )}

            {!!userPermissions[PERMISSION_CALENDAR]?.update &&
                !ended &&
                calendarMeeting.status !== 'canceled' &&
                canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'update') && (
                    <div className='flex flex-col max-w-3xl w-full mx-auto px-8 mb-6'>
                        <BaseButton
                            className='btn-primary-outline'
                            textValue='Cancel Meeting'
                            onClick={handleCancelConfirmation}
                        />
                    </div>
                )}

            {calendarMeeting?.user && (
                <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between mb-6'>
                    <div className='text-lg font-bold text-theme1-title2'>
                        <div>Added By</div>
                        <div className='text-base text-theme1-primary mt-1'>
                            <Link to={`/contacts/view/${calendarMeeting.user.contact_id}`}>
                                {calendarMeeting?.user.name}
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {cancelVisible && (
                <MeetingCancelConfirmation
                    cancelVisible={cancelVisible}
                    setCancelVisible={setCancelVisible}
                    meeting={calendarMeeting}
                />
            )}

            {selectedOrganization && (
                <OrganizationsAIInfoOverlay
                    showOrgInfoOverlay={showOrgInfoOverlay}
                    setShowOrgInfoOverlay={setShowOrgInfoOverlay}
                    selectedOrganization={selectedOrganization}
                    setSelectedOrganization={setSelectedOrganization}
                />
            )}
        </AuthLayout>
    );
};

export default ViewMeeting;
